import antigenKitContentsHealgen from '../images/antigenKitContents_healgen.jpg'
import antigenKitContentsHughes from '../images/antigenKitContents_hughes.jpg'
import antigenTestResultHealgen from '../images/antigenTestResult_healgen.jpg'
import antigenTestResultHughes from '../images/antigenTestResult_hughes.jpg'
import cassetteLabelledHealgen from '../images/cassetteLabelled_healgen.jpg'
import cassetteLabelledHughes from '../images/cassetteLabelled_hughes.jpg'
import travelrapid_step1Healgen from '../images/travelrapid_step1_healgen.svg'
import travelrapid_step1Hughes from '../images/travelrapid_step1_hughes.jpg'
import travelrapid_step2 from '../images/travelrapid_step2.svg'
import travelrapid_step3 from '../images/travelrapid_step3.svg'
import travelrapid_step4 from '../images/travelrapid_step4.svg'
import travelrapid_step5Healgen from '../images/travelrapid_step5_healgen.svg'
import travelrapid_step5Hughes from '../images/travelrapid_step5_hughes.svg'
import travelrapid_step6 from '../images/travelrapid_step6.svg'
import travelrapid_step6b from '../images/travelrapid_step6b.svg'
import travelrapid_step7 from '../images/travelrapid_step7.svg'
import wait1 from '../images/travelrapid_wait1.svg'
import wait15 from '../images/travelrapid_wait15.svg'

interface AntigenTestGuideProps {
  kitId: string
  sku: 'rapid_travel' | 'rapid_travel_hughes'
  supervised: boolean
}
const AntigenTestGuide = ({ kitId, sku, supervised }: AntigenTestGuideProps) => {
  const KitId = () => <span className="is-family-monospace has-text-primary">{kitId}</span>

  if (supervised) {
    return (
      <>
        <h2 className="title is-4 has-text-midnightBlue">Make sure your picture has been taken correctly</h2>
        <img
          className="is-margin-0-auto"
          src={sku === 'rapid_travel_hughes' ? antigenTestResultHughes : antigenTestResultHealgen}
          width="300px"
          alt="Passport and cassette example"
        />
        <div className="content">
          <ul>
            <li>
              Your picture should be taken between 15 and {sku === 'rapid_travel_hughes' ? '30' : '20'} minutes after
              loading the sample onto your test cassette.
            </li>
            <li>
              Place your completed and labelled test cassette next to your passport or ID document, opened at the page
              with your personal details, as shown in the example above.
            </li>
            <li>Take a photo of the cassette and passport / ID document.</li>
            <li>
              Make sure that the test cassette and details on your ID document are clearly visible and are not covered
              by any shadows.
            </li>
          </ul>
        </div>
      </>
    )
  }

  return (
    <>
      <h1 className="title is-spaced has-text-midnightBlue">
        Instructions for Antigen test (ID: <span className="is-family-monospace has-text-weight-light">{kitId}</span>)
      </h1>
      <p className="subtitle">
        Follow our guide to take your rapid antigen test. For more detailed instructions, please see the manufacturer's
        instructions contained within your kit.
      </p>

      <div className="content">
        <h2 className="title is-4 has-text-midnightBlue">Preparation</h2>
        <img
          className="is-margin-0-auto"
          src={sku === 'rapid_travel_hughes' ? antigenKitContentsHughes : antigenKitContentsHealgen}
          width="300px"
          alt="Kit contents"
        />
        <ul>
          <li>
            You'll need about 30 minutes to complete this procedure and will need a way to take a digital picture of
            your test (e.g. with a smartphone camera).
          </li>
          <li>You'll also need your passport (or ID document) and a ballpoint pen.</li>
          <li>
            Make sure you have your kit box, test cassette, tube containing buffer liquid, tube cap and sterile swab.
          </li>
        </ul>

        <h2 className="title is-4 has-text-midnightBlue">Step 1</h2>
        <img
          className="is-margin-0-auto"
          src={sku === 'rapid_travel_hughes' ? travelrapid_step1Hughes : travelrapid_step1Healgen}
          width="300px"
          alt="Antigen Test Box"
        />
        <ul>
          <li>Place the tube into the hole in the side of your kit box to use it as a stand.</li>
          <li>Remove the foil lid from the top of the tube. Be careful not to spill any of the liquid inside.</li>
        </ul>

        <h2 className="title is-4 has-text-midnightBlue">Step 2</h2>
        <img
          className="is-margin-0-auto"
          src={sku === 'rapid_travel_hughes' ? cassetteLabelledHughes : cassetteLabelledHealgen}
          width="300px"
          alt="Cassette labelled"
        />
        <ul>
          <li>Take your test cassette out of the foil packet.</li>
          <li>
            Use a ballpoint pen to clearly write your kit ID (
            <KitId />) on the side of your test cassette.
          </li>
        </ul>
        <div className="message is-warning">
          <div className="message-body">
            Writing your kit ID on the cassette is <em>required</em> for us to send your certificate.
          </div>
        </div>

        <h2 className="title is-4 has-text-midnightBlue">Step 3</h2>
        <img className="is-margin-0-auto" src={travelrapid_step2} width="300px" alt="Cassette labelled" />
        <ul>
          <li>Open the swab packet where indicated and pull out by the plastic end. Don't touch the absorbent tip.</li>
        </ul>

        <h2 className="title is-4 has-text-midnightBlue">Step 4</h2>
        <img className="is-margin-0-auto" src={travelrapid_step3} width="300px" alt="Nasal swab" />
        <ul>
          <li>
            Gently insert the absorbent tip of the swab into one nostril ensuring that the entire tip is inside your
            nostril (about 2cm).
          </li>
          <li>Roll the swab at least 5 times around the inside of your nostril.</li>
          <li>Remove the swab from your nostril and repeat the swabbing process on your other nostril.</li>
        </ul>
        <img className="is-margin-0-auto" src={travelrapid_step4} width="100px" alt="Nasal swab other nostril" />
        <ul>
          <li>Remove the swab from your nostril and repeat the swabbing process on your other nostril.</li>
        </ul>

        <h2 className="title is-4 has-text-midnightBlue">Step 5</h2>
        <img
          className="is-margin-0-auto"
          src={sku === 'rapid_travel_hughes' ? travelrapid_step5Hughes : travelrapid_step5Healgen}
          width={sku === 'rapid_travel_hughes' ? '300px' : '150px'}
          alt="Swab in tube"
        />
        <ul>
          <li>Place the tip of the swab into the tube containing buffer.</li>
          {sku === 'rapid_travel' && (
            <li>
              Mix well by rolling the swab at least 6 times around the inside of the tube while pressing the tip firmly
              against the bottom and sides of the tube.
            </li>
          )}
          {sku === 'rapid_travel_hughes' && (
            <>
              <li>Mix well by swirling the swab inside the tube of liquid for 30 seconds.</li>
              <li>Rotate the swab 5 times while squeezing it through the tube.</li>
            </>
          )}
        </ul>
        {sku === 'rapid_travel' && (
          <>
            <img className="is-margin-0-auto" src={wait1} width="50px" alt="Wait for one minute" />
            <ul>
              <li>Leave the swab sitting inside the tube for 1 minute.</li>
            </ul>
          </>
        )}

        <h2 className="title is-4 has-text-midnightBlue">Step 6</h2>
        <img className="is-margin-0-auto" src={travelrapid_step6} width="150px" alt="Swab in tube" />
        <ul>
          <li>
            Remove the swab from the liquid and squeeze it firmly several times through the tube to squeeze out as much
            fluid as possible.
          </li>
        </ul>
        <img className="is-margin-0-auto" src={travelrapid_step6b} width="50px" alt="Put lid on" />
        <ul>
          {sku === 'rapid_travel' && <li>Firmly place the lid on the tube.</li>}
          {sku === 'rapid_travel_hughes' && <li>Firmly place the lid on the tube and remove the small rubber tip.</li>}
        </ul>

        <h2 className="title is-4 has-text-midnightBlue">Step 7</h2>
        <img className="is-margin-0-auto" src={travelrapid_step7} width="150px" alt="Swab in tube" />
        <ul>
          <li>Add 4 drops of fluid to the sample well of your cassette by squeezing the tube gently.</li>
        </ul>
        <img className="is-margin-0-auto" src={wait15} width="50px" alt="Wait 15 minutes" />
        <ul>
          <li>
            Start a timer (e.g. on your phone) and wait 15 minutes.
            <div className="message is-warning">
              <div className="message-body">
                After
                <em> {sku === 'rapid_travel_hughes' ? '30' : '20'} minutes </em>
                the test is invalid and must be discarded, make sure you take your photo before then.
              </div>
            </div>
          </li>
        </ul>

        <h2 className="title is-4 has-text-midnightBlue">Step 8</h2>
        <img
          className="is-margin-0-auto"
          src={sku === 'rapid_travel_hughes' ? antigenTestResultHughes : antigenTestResultHealgen}
          width="300px"
          alt="Passport and cassette example"
        />
        <ul>
          <li>
            Place your completed and labelled test cassette next to your passport or ID document, opened at the page
            with your personal details, as shown in the example above.
          </li>
          <li>Take a photo of the cassette and passport / ID document.</li>
          <li>
            Make sure that the test cassette and details on your ID document are clearly visible and are not covered by
            any shadows.
          </li>
        </ul>
      </div>
    </>
  )
}

export default AntigenTestGuide
